import React from 'react';
import { useTranslation } from 'react-i18next';
import { CircleAlert, CircleCheck } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import FileNameList from './FileNameList';
import UploadCard from './UploadCard';

export interface UploadResults {
  succeeded: string[];
  failed: { file: string; error: Error }[];
}

interface UploadResultsCardProps {
  results: UploadResults;
}

const UploadResultsCard: React.FC<UploadResultsCardProps> = ({ results }) => {
  const { t } = useTranslation();

  return (
    <UploadCard title={t('testUpload.results')}>
      <div className="mb-4 flex flex-wrap gap-2">
        <Badge variant="success" className="gap-1.5 py-1">
          <CircleCheck className="size-3.5" strokeWidth={2} />
          <span className="revy-cjk revy-figure">
            {t('testUpload.successCount', { count: results.succeeded.length })}
          </span>
        </Badge>
        <Badge variant="destructive" className="gap-1.5 py-1">
          <CircleAlert className="size-3.5" strokeWidth={2} />
          <span className="revy-cjk revy-figure">
            {t('testUpload.failureCount', { count: results.failed.length })}
          </span>
        </Badge>
      </div>

      {results.succeeded.length > 0 && (
        <>
          <h3 className="revy-cjk mb-2 text-xs font-semibold leading-[1.5] text-muted-foreground">
            {t('testUpload.succeededFiles')}
          </h3>
          <div className="mb-4">
            <FileNameList
              items={results.succeeded.map((fileName, index) => ({
                id: `${index}-${fileName}`,
                primary: fileName,
                icon: <CircleCheck className="size-4 text-success" strokeWidth={1.9} />,
              }))}
            />
          </div>
        </>
      )}

      {results.failed.length > 0 && (
        <>
          <h3 className="revy-cjk mb-2 text-xs font-semibold leading-[1.5] text-muted-foreground">
            {t('testUpload.failedFiles')}
          </h3>
          <FileNameList
            items={results.failed.map(({ file, error }, index) => ({
              id: `${index}-${file}`,
              primary: file,
              secondary: error.message,
              icon: <CircleAlert className="size-4 text-destructive" strokeWidth={1.9} />,
            }))}
          />
        </>
      )}
    </UploadCard>
  );
};

export default UploadResultsCard;
